import React, { useEffect, useState } from "react";

//REACT QUERY
import { useQueryClient } from "@tanstack/react-query";

//ICONS
import { Hotspot, Scene, TourData } from "../../../Types";
import { UseGetTour, UseSaveTour } from "../../../api/calls/Queries";

import OtherHotspots from "./OtherHotspots";
import {
  useCurrentSceneStore,
  useEditingElementStore,
} from "../../../api/Zustand";
import { RightArrow } from "../../Icons/left-side-bar-icons";
import { EditPencil, Trashbin } from "../../Icons/gui-elements-icons";
import { getLanguageFile } from "../../common/GetLanguageFile";

function WaypointEditing({ id, tourID }: { id: string; tourID: number }) {
  const { data: tourData, isLoading: tourDataLoading } = UseGetTour(tourID);

  const { mutateAsync: saveTourData, isPending: tourIsSaving } = UseSaveTour();
  const queryClient = useQueryClient();

  const { currentSceneID } = useCurrentSceneStore();

  const [currentWay, setCurrentWay] = useState<Hotspot>();

  const { setEditingElem } = useEditingElementStore();

  useEffect(() => {
    if (tourData) {
      let temp = tourData.scenes
        .find((one: Scene) => one.id === currentSceneID)!
        .hotspots.find((one: Hotspot) => one.id === id);

      setCurrentWay(temp);
    }
  }, [currentSceneID, id, tourData]);

  const handleDelete = async () => {
    const tempSceneSpots = tourData!.scenes
      .find((one: Scene) => one.id === currentSceneID)!
      .hotspots.filter((spot: Hotspot) => spot.id !== currentWay!.id);

    const newTourData = queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData!.scenes.map((scene: Scene) =>
        scene.id === currentSceneID
          ? { ...scene, hotspots: tempSceneSpots }
          : scene
      ),
    });
    setEditingElem("scene", currentSceneID);
    saveTourData(newTourData as TourData);
  };

  const onDirectionChange = async (dir: String) => {
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: [
        ...tourData!.scenes.map((sc: Scene) =>
          sc.id === currentSceneID
            ? {
                ...sc,
                hotspots: sc.hotspots.map((hs: Hotspot) =>
                  hs.id === currentWay!.id ? { ...hs, dir: dir } : hs
                ),
              }
            : sc
        ),
      ],
    });
  };

  const onTargetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: [
        ...tourData!.scenes.map((sc: Scene) =>
          sc.id === currentSceneID
            ? {
                ...sc,
                hotspots: sc.hotspots.map((hs: Hotspot) =>
                  hs.id === currentWay!.id
                    ? { ...hs, target: JSON.parse(e.target.value) }
                    : hs
                ),
              }
            : sc
        ),
      ],
    });
  };

  //
  const handlePosEdit = async () => {
    if (currentWay) {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: [
          ...tourData!.scenes.map((sc: Scene) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs: Hotspot) =>
                    hs.id === currentWay!.id
                      ? {
                          ...hs,
                          EDIT_POS: currentWay.EDIT_POS ? undefined : true,
                        }
                      : hs
                  ),
                }
              : sc
          ),
        ],
      });
    }
  };

  const handleWaypointUpdate = async () => {
    const newTourData = queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData?.scenes.map((sc: Scene) =>
        sc.id === currentSceneID
          ? {
              ...sc,
              hotspots: sc.hotspots.map((hs: Hotspot) =>
                hs.id === id
                  ? {
                      ...currentWay,
                      EDIT_POS: undefined,
                      isNew: undefined,
                    }
                  : hs
              ),
            }
          : sc
      ),
    });
    saveTourData(newTourData as TourData);
  };

  const currentScene = React.useMemo(() => {
    return tourData?.scenes.find((one) => one.id === currentSceneID);
  }, [tourData?.scenes, currentSceneID]);

  const sameGroupMaps = React.useMemo(() => {
    return tourData!.scenes.filter(
      (scn: Scene) =>
        scn.id !== currentSceneID && scn.group === currentScene?.group
    );
  }, [tourData, currentSceneID, currentScene?.group]);

  const otherGroupsMaps = React.useMemo(() => {
    return tourData!.scenes.filter(
      (scn: Scene) =>
        scn.id !== currentSceneID && scn.group !== currentScene?.group
    );
  }, [tourData, currentSceneID, currentScene?.group]);

  return (
    <div className="right-side-panel-editor">
      <h3>WAYPOINT</h3>

      {currentWay && (
        <div
          style={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: ".75rem",
            }}
          >
            <label>
              {getLanguageFile("right_panel.assign_scene")}
              <select
                onChange={(e) => onTargetChange(e)}
                value={currentWay.target}
              >
                {!tourData!.scenes.find(
                  (one: Scene) => one.id === currentWay.target
                ) &&
                  currentWay.target && (
                    <option disabled>
                      La scena bersaglio è stata eliminata
                    </option>
                  )}

                {!currentWay.target && (
                  <option disabled value="">
                    {getLanguageFile("right_panel.assign_scene_placeholder")}
                  </option>
                )}

                {sameGroupMaps.length > 0 && (
                  <option disabled>
                    {getLanguageFile("right_panel.same_group")}
                  </option>
                )}
                {sameGroupMaps.map((scene: Scene) => (
                  <option key={scene.id} value={scene.id}>
                    {scene.title}
                    {!tourData!.scenes
                      .map((scn: Scene) => {
                        return scn.hotspots;
                      })
                      .flat()
                      .filter((spt: Hotspot) => spt.type === "transition")
                      .some((spot: Hotspot) => spot.target === scene.id) &&
                    tourData?.startingScene !== scene.id
                      ? " - " + getLanguageFile("right_panel.inaccessible")
                      : null}
                  </option>
                ))}

                {otherGroupsMaps.length > 0 && (
                  <option disabled value="">
                    {getLanguageFile("right_panel.other_groups")}
                  </option>
                )}
                {otherGroupsMaps.map((scene: Scene) => (
                  <option key={scene.id} value={scene.id}>
                    {scene.title}
                    {!tourData!.scenes
                      .map((scene: Scene) => {
                        return scene.hotspots;
                      })
                      .flat()
                      .filter((spt: Hotspot) => spt.type === "transition")
                      .some((spot: Hotspot) => spot.target === scene.id) &&
                    tourData?.startingScene !== scene.id
                      ? " - " + getLanguageFile("right_panel.inaccessible")
                      : null}
                  </option>
                ))}
              </select>
            </label>

            <DirButtons
              currentWay={currentWay}
              onDirectionChange={onDirectionChange}
            />

            <button
              id="edit"
              className={currentWay.EDIT_POS ? "active" : undefined}
              onClick={handlePosEdit}
            >
              {currentWay.EDIT_POS ? (
                getLanguageFile("right_panel.save_position")
              ) : (
                <>
                  {getLanguageFile("right_panel.edit_position")} <EditPencil />
                </>
              )}
            </button>

            <div style={{ display: "flex" }}>
              <button
                disabled={tourIsSaving}
                id="save"
                onClick={handleWaypointUpdate}
                style={{ flex: 1 }}
              >
                {currentWay.isNew
                  ? getLanguageFile("right_panel.create_pop")
                  : tourIsSaving
                  ? getLanguageFile("generics.saving")
                  : getLanguageFile("generics.save")}
              </button>

              <button id="delete" onClick={handleDelete} style={{ flex: 1 }}>
                {currentWay.isNew ? (
                  getLanguageFile("generics.cancel")
                ) : (
                  <>
                    {getLanguageFile("generics.delete")} <Trashbin />
                  </>
                )}
              </button>
            </div>
            <div
              style={{
                opacity: currentWay.EDIT_POS ? ".5" : "1",
              }}
            ></div>
          </div>

          <OtherHotspots
            icon={<RightArrow width="1rem" height="1rem" />}
            currentHotspot={currentWay}
            tourData={tourData!}
            type="transition"
          ></OtherHotspots>
        </div>
      )}
    </div>
  );
}
export default WaypointEditing;

const dirBtns = [
  { d: "L", r: -180 },
  { d: "B", r: 90 },
  { d: "T", r: -90 },
  { d: "R", r: 0 },
];

const DirButtons = (props: {
  currentWay: Hotspot;
  onDirectionChange: (dir: String) => Promise<void>;
}) => {
  return (
    <div>
      {getLanguageFile("right_panel.direction")}
      {dirBtns.map((dir: any, index) => (
        <button
          key={dir.d}
          id="dir-selection"
          onClick={() => props.onDirectionChange(dir.d)}
          disabled={props.currentWay.dir === dir.d}
        >
          <RightArrow
            width="1.25em"
            height="1.25em"
            style={{ transform: `rotate(${dir.r}deg)` }}
          />
        </button>
      ))}
    </div>
  );
};
